import {RootState} from "@/store/Store";
import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {v4 as uuidv4} from 'uuid';
import {OrderDNATestFormConfig} from "@/features/OrderDNATestForm/Config/OrderDNATestFormConfig";
import {ParticipantInterface} from "@/api/Orders/OpenedOrder/Interfaces/ParticipantInterface";
import dayjs from "dayjs";
import {PriceListServiceInterface} from "@/api/Orders/PriceListServices/Interfaces/PriceListServiceInterface";
import {
    OrderAdditionalServiceInterface
} from "@/api/List/OrderAdditionalServices/Interfaces/OrderAdditionalServiceInterface";
import {DealProductInterface} from "@/api/Orders/OpenedOrder/Interfaces/DealProductInterface";
import {

    CalculateSelectionsInterface
} from "@/api/Orders/NewCalculatePrice/CalculateSelections/CalculateSelectionsApiSlice";
import {infiniteCount} from "@/config/Other";
import {
    SampleTypeInterface,
    updateMatchingRulePayloadAction
} from "@/features/OrderDNATestForm/Slice/OrderDNATestFormSlice";
import {
    AttributeRuleForNParticipantsFlatItemInterface
} from "@/api/ReferenceBooks/AttributeRuleForNParticipants/List/Interfaces/AttributeRuleForNParticipantsListInterface";
import {
    FamilyStepsActiveItemInterface
} from "@/api/ReferenceBooks/FamilySteps/List/Interfaces/FamilyStepsListInterface";
import {hasNonStandardSample} from "@/features/Orders/helpers/nonStandardSample";
import {isKinshipCategoryFlowEnabled} from "@/features/OrderDNATestForm/Helpers/kinshipCategoryFlow";
import {
    getTestTypeAlias,
    isFieldEnabledByRule,
} from "@/features/OrderDNATestForm/Helpers/orderFormFieldRules";
import {OrderFormFieldRuleInterface} from "@/api/ReferenceBooks/Rules/AnalysisOrderForm/OrderFormFields";
import {PedigreeSchema} from "@/features/PedigreeEditor";
import {
    mapNotInWorkSampleFromApi,
    mapWorkingSampleFromApi,
} from "@/common/Orders/ParticipantSamples/ParticipantSampleMappers";
import type {
    ParticipantSampleApiInterface,
    ParticipantSampleNotInWorkApiInterface,
} from "@/common/Orders/ParticipantSamples/ParticipantSampleTypes";
import {areParticipantSamplePartsValid} from "@/common/Orders/ParticipantSamples/ParticipantSamplePartsValidation";
import {isParticipantCopy} from "@/common/Orders/ParticipantCopies";
import {
    getInvalidParticipantSamples,
} from "@/common/Orders/ParticipantSamples/ParticipantSampleNormalization";

export const notSelectedId = -1;

export type EditDNATestFormState = ReturnType<typeof getInitialState>;

export type AllEditStepsFields =
    keyof EditDNATestFormState["step1"]
    | keyof EditDNATestFormState["step2"]
    | keyof EditDNATestFormState["step3"];

type KeysWithValue<T> = {
    [K in keyof T]: T[K] extends { value: unknown } ? K : never;
}[keyof T];

// Типы для каждого шага (для удобства)
type Step1 = EditDNATestFormState["step1"];
type Step2 = EditDNATestFormState["step2"];
type Step3 = EditDNATestFormState["step3"];

export type AllEditStepsFieldsWithValue = KeysWithValue<Step1> | KeysWithValue<Step2> | KeysWithValue<Step3>

// Discriminated union для payload: для каждого step + name + value точный тип
export type SetFormValuePayload =
    | {
    [K in KeysWithValue<Step1>]: {
        step: "step1";
        name: K;
        value?: Step1[K]["value"];
        error?: Step1[K]["error"];
        valid?: Step1[K]["valid"];
    }
}[KeysWithValue<Step1>]
    | {
    [K in KeysWithValue<Step2>]: {
        step: "step2";
        name: K;
        value?: Step2[K]["value"];
        error?: Step2[K]["error"];
        valid?: Step2[K]["valid"];
    }
}[KeysWithValue<Step2>]
    | {
    [K in KeysWithValue<Step3>]: {
        step: "step3";
        name: K;
        value?: Step3[K]["value"];
        error?: Step3[K]["error"];
        valid?: Step3[K]["valid"];
    }
}[KeysWithValue<Step3>];

function getInitialState() {
    return {
        step: 0,
        highlightedField: null as string | null,
        highlightedParticipantId: null as string | null,
        initialSelections: null as CalculateSelectionsInterface | null,
        availableSampleIds: null as number[] | null,
        orderFormFieldRules: [] as OrderFormFieldRuleInterface[],

        step1: {
            orderId: {
                value: "",
                valid: true,
                error: "",
            },
            partner: {
                value: notSelectedId,
                valid: false,
                error: "",
            },
            testType: {
                value: "",
                valid: false,
                error: "",
            },
            testTypeOther: {
                value: "",
                valid: false,
                error: "",
            },
            additionalServices: {
                value: [] as OrderAdditionalServiceInterface["id"][],
                valid: false,
                error: "",
            },
            legalStatus: {
                value: "",
                valid: false,
                error: "",
            },
            attribute: {
                value: "",
                valid: false,
                error: "",
            },
            researchPeriod: {
                value: "",
                valid: false,
                error: "",
            },
            priceListServices: {
                value: [] as PriceListServiceInterface["id"][],
                valid: true,
                error: "",
            },
            products: {
                value: [] as DealProductInterface[],
                valid: true,
                error: "",
            }
        },
        step2: {
            participantId: "",
            profileResearch: {
                value: notSelectedId,
                valid: true,
                error: "",
            },

            fioOrNickname: {
                value: "",
                valid: false,
                error: "",
            },
            gender: {
                value: notSelectedId,
                valid: false,
                error: "",
            },
            birthday: {
                value: "",
                valid: true,
                error: "",
            },
            degreeOfKinship: {
                value: notSelectedId,
                valid: false,
                error: "",
            },
            degreeOfKinshipPreviousOrderNumber: {
                value: "",
                valid: false,
                error: "",
            },
            sampleType: {
                value: [] as SampleTypeInterface[],
                valid: false,
                error: "",
            },
            sampleTypeNotInWork: {
                value: [] as SampleTypeInterface[],
                valid: true,
                error: "",
            },
            sampleTypeOther: {
                value: "",
                valid: false,
                error: "",
            },
            sampleCollectionDate: {
                value: "",
                valid: true,
                error: "",
            },
            documentType: {
                value: notSelectedId,
                valid: false,
                error: "",
            },
            documentNumber: {
                value: "",
                valid: false,
                error: "",
            },
            whoCollectedSamples: {
                value: notSelectedId,
                valid: true,
                error: "",
            },
            isExternalSampling: {
                value: false,
                valid: true,
                error: "",
            },
            commentForAllSamples: {
                value: "",
                valid: true,
                error: "",
            },

            isUserNewOrEdit: "new" as userNewOrEdit,

            testParticipants: [] as TestParticipantInterface[],

            participantFiles: [
                {
                    id: uuidv4(),
                    file: null,
                    meaning: "",
                },
            ] as UploadingFileInterface[],

            isFormVisible: false,

            editingParticipantId: "",
        },
        orderPrice: null as number | null,
        existingProtocolGet: false,
        existingDeterminationCourt: false,
        participantsMin: infiniteCount as number | null,
        participantsMax: infiniteCount as number | null,
        sampleMin: infiniteCount as number | null,
        sampleMax: infiniteCount as number | null,

        step3: {
            isThirdStepValid: true,

            //additional info
            firstTestType: {
                value: "",
                valid: false,
                error: "",
            },
            pregnancyPeriod: {
                value: "",
                valid: false,
                error: "",
            },
            pregnancyPeculiarities: {
                value: "0",
                valid: false,
                error: "",
            },
            describeKinshipTies: {
                value: "",
                valid: false,
                error: "",
            },
            anotherImportantInfo: {
                value: "",
                valid: false,
                error: "",
            },
            trackNumber: {
                value: "",
                valid: false,
                error: "",
            },
            civilCaseNumber: {
                value: "",
                valid: false,
                error: "",
            },
            courtName: {
                value: "",
                valid: false,
                error: "",
            },

            files: [
                {
                    id: uuidv4(),
                    file: null,
                    meaning: "",
                },
                {
                    id: uuidv4(),
                    file: null,
                    meaning: "",
                },
            ] as UploadingFileInterface[],

            endCustomerAgreedDateOfRecording: {
                value: "",
                valid: true,
                error: "",
            },

            matchingRules: [] as AttributeRuleForNParticipantsFlatItemInterface[],
            matchingRuleId: "notChosen",
            kinshipScheme: "",
            familyStepInDoubt: null as FamilyStepsActiveItemInterface | null,
            savedKinshipCategoryRuleId: null as number | null,
            kinshipSchemaJson: null as PedigreeSchema | null,

            // CategorySelector state
            categorySelectionPath: [] as Array<{ containerId: number; selectedItemId: number | null }>,
            currentCategoryStep: 0,
            finalSelectedRule: null as AttributeRuleForNParticipantsFlatItemInterface | null,
        },
    };
}

export const EditDNATestFormSlice = createSlice({
    name: "EditDNATestFormSlice",
    initialState: getInitialState(),
    reducers: {
        setStep: (state, action: PayloadAction<number>) => {
            state.step = action.payload;
        },
        setHighlightedField: (state, action: PayloadAction<string | null>) => {
            state.highlightedField = action.payload;
        },
        setHighlightedParticipantId: (state, action: PayloadAction<string | null>) => {
            state.highlightedParticipantId = action.payload;
        },

        setFormValue: (state: EditDNATestFormState, action: PayloadAction<SetFormValuePayload>) => {
            const {step, name, value} = action.payload;

            switch (step) {
                case "step1":
                    if (value !== undefined) state.step1[name].value = value;
                    break;
                case "step2":
                    if (value !== undefined) state.step2[name].value = value;
                    break;
                case "step3":
                    if (value !== undefined) state.step3[name].value = value;
                    break;
            }
        },
        setParticipantSampleLists: (state, action: PayloadAction<{
            sampleType: SampleTypeInterface[];
            sampleTypeNotInWork: SampleTypeInterface[];
        }>) => {
            state.step2.sampleType.value = action.payload.sampleType;
            state.step2.sampleTypeNotInWork.value = action.payload.sampleTypeNotInWork;
        },
        clearParticipantSamples: (state) => {
            state.step2.sampleType.value = [];
            state.step2.sampleTypeNotInWork.value = [];
            state.step2.sampleTypeOther.value = "";
            state.step2.testParticipants = state.step2.testParticipants.map(participant => ({
                ...participant,
                sampleType: [],
                sampleTypeNotInWork: [],
                sampleTypeOther: "",
            }));
        },
        setAvailableSampleIds: (state, action: PayloadAction<number[]>) => {
            state.availableSampleIds = action.payload;
        },
        setFormError: (state: EditDNATestFormState, action: PayloadAction<SetFormValuePayload>) => {
            const {step, name, error} = action.payload;

            switch (step) {
                case "step1":
                    if (error !== undefined) state.step1[name].error = error;
                    break;
                case "step2":
                    if (error !== undefined) state.step2[name].error = error;
                    break;
                case "step3":
                    if (error !== undefined) state.step3[name].error = error;
                    break;
            }
        },
        setFormValidation: (state: EditDNATestFormState, action: PayloadAction<SetFormValuePayload>) => {
            const {step, name, valid} = action.payload;

            switch (step) {
                case "step1":
                    if (valid !== undefined) state.step1[name].valid = valid;
                    break;
                case "step2":
                    if (valid !== undefined) state.step2[name].valid = valid;
                    break;
                case "step3":
                    if (valid !== undefined) state.step3[name].valid = valid;
                    break;
            }
        },

        addTestParticipant: (state, action) => {
            const id = uuidv4();
            const fioOrNickname = state.step2.fioOrNickname.value;
            const gender = state.step2.gender.value;
            const birthday = state.step2.birthday.value;
            const degreeOfKinship = state.step2.degreeOfKinship.value;
            const degreeOfKinshipPreviousOrderNumber = state.step2.degreeOfKinshipPreviousOrderNumber.value;
            const sampleType = state.step2.sampleType.value;
            const sampleTypeNotInWork = state.step2.sampleTypeNotInWork.value;
            const sampleTypeOther = state.step2.sampleTypeOther.value;
            const sampleCollectionDate = state.step2.sampleCollectionDate.value;
            const whoCollectedSamples = state.step2.whoCollectedSamples.value;
            const documentType = state.step2.documentType.value;
            const documentNumber = state.step2.documentNumber.value;
            const profileResearch = state.step2.profileResearch.value;

            state.step2.testParticipants.push({
                id, fioOrNickname, gender, birthday, degreeOfKinship,
                degreeOfKinshipPreviousOrderNumber, sampleType, sampleTypeNotInWork,
                sampleTypeOther, sampleCollectionDate, documentType, documentNumber, whoCollectedSamples,
                profileResearch, parent_participant_id: null,
                isExternalSampling: state.step2.isExternalSampling.value,
            });
        },
        clearAll: (state) => {
            const newInitialState = getInitialState();
            state.step = newInitialState.step;
            state.step1 = newInitialState.step1;
            state.step2 = newInitialState.step2;
            state.step3 = newInitialState.step3;
        },
        clearStep1: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.testType = newInitialState.step1.testType;
            state.step1.testTypeOther = newInitialState.step1.testTypeOther;
            state.step1.additionalServices = newInitialState.step1.additionalServices;
            state.step1.legalStatus = newInitialState.step1.legalStatus;
            state.step1.attribute = newInitialState.step1.attribute;
            state.step1.researchPeriod = newInitialState.step1.researchPeriod;
            state.step3.firstTestType = newInitialState.step3.firstTestType;
            state.step3.pregnancyPeriod = newInitialState.step3.pregnancyPeriod;
            state.step3.describeKinshipTies = newInitialState.step3.describeKinshipTies;
            state.participantsMin = newInitialState.participantsMin;
            state.participantsMax = newInitialState.participantsMax;
            state.sampleMin = newInitialState.sampleMin;
            state.sampleMax = newInitialState.sampleMax;
            state.availableSampleIds = newInitialState.availableSampleIds;
        },
        clearTestType: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.testType = newInitialState.step1.testType;
            state.participantsMin = newInitialState.participantsMin;
            state.participantsMax = newInitialState.participantsMax;
            state.sampleMin = newInitialState.sampleMin;
            state.sampleMax = newInitialState.sampleMax;
            state.availableSampleIds = newInitialState.availableSampleIds;
        },
        clearLegalStatus: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.legalStatus = newInitialState.step1.legalStatus;
        },
        clearAttribute: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.attribute = newInitialState.step1.attribute;
        },
        clearResearchPeriod: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.researchPeriod = newInitialState.step1.researchPeriod;
        },
        clearAdditionalServices: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();
            state.step1.additionalServices = newInitialState.step1.additionalServices;
        },
        clearParticipantForm: (state, action: PayloadAction<void>) => {
            const newInitialState = getInitialState();

            state.step2.fioOrNickname = newInitialState.step2.fioOrNickname;
            state.step2.gender = newInitialState.step2.gender;
            state.step2.birthday = newInitialState.step2.birthday;
            state.step2.degreeOfKinship = newInitialState.step2.degreeOfKinship;
            state.step2.degreeOfKinshipPreviousOrderNumber = newInitialState.step2.degreeOfKinshipPreviousOrderNumber;
            state.step2.sampleType = newInitialState.step2.sampleType;
            state.step2.sampleTypeNotInWork = newInitialState.step2.sampleTypeNotInWork;
            state.step2.sampleTypeOther = newInitialState.step2.sampleTypeOther;
            state.step2.sampleCollectionDate = newInitialState.step2.sampleCollectionDate;
            state.step2.whoCollectedSamples = newInitialState.step2.whoCollectedSamples;
            state.step2.isExternalSampling = newInitialState.step2.isExternalSampling;
            state.step2.documentType = newInitialState.step2.documentType;
            state.step2.documentNumber = newInitialState.step2.documentNumber;
            state.step2.profileResearch = newInitialState.step2.profileResearch;
            state.step2.participantFiles = newInitialState.step2.participantFiles;
        },
        saveEditedTestParticipant: (state, action) => {
            state.step2.testParticipants = state.step2.testParticipants.map(participant => {
                if (participant.id !== state.step2.participantId) {
                    return participant;
                }
                const editedParticipant: TestParticipantInterface = {
                    ...participant,
                    fioOrNickname: state.step2.fioOrNickname.value,
                    gender: state.step2.gender.value,
                    birthday: state.step2.birthday.value,
                    degreeOfKinship: state.step2.degreeOfKinship.value,
                    degreeOfKinshipPreviousOrderNumber: state.step2.degreeOfKinshipPreviousOrderNumber.value,
                    sampleType: state.step2.sampleType.value,
                    sampleTypeNotInWork: state.step2.sampleTypeNotInWork.value,
                    sampleTypeOther: state.step2.sampleTypeOther.value,
                    sampleCollectionDate: state.step2.sampleCollectionDate.value,
                    whoCollectedSamples: state.step2.whoCollectedSamples.value,
                    documentType: state.step2.documentType.value,
                    documentNumber: state.step2.documentNumber.value,
                    profileResearch: state.step2.profileResearch.value,
                    isExternalSampling: state.step2.isExternalSampling.value,
                };
                return editedParticipant;
            });
        },
        setParticipantFormStatus: (state, action) => {
            state.step2.isUserNewOrEdit = action.payload.status;
        },
        deleteTestParticipant: (state, action) => {
            state.step2.testParticipants = state.step2.testParticipants.filter(participant => participant.id !== action.payload.id);
        },
        showParticipantForm: (state) => {
            state.step2.isFormVisible = true;
        },
        hideParticipantForm: (state) => {
            state.step2.isFormVisible = false;
        },
        editTestParticipant: (state, action) => {
            const id = action.payload.id;
            const participant = state.step2.testParticipants.find(participant => participant.id === id) as TestParticipantInterface;
            state.step2.editingParticipantId = participant.id;
            state.step2.participantId = participant.id;
            state.step2.fioOrNickname.value = participant.fioOrNickname;
            state.step2.gender.value = participant.gender;
            state.step2.birthday.value = participant.birthday === null ? "" : participant.birthday;
            state.step2.degreeOfKinship.value = participant.degreeOfKinship;
            state.step2.degreeOfKinshipPreviousOrderNumber.value = participant.degreeOfKinshipPreviousOrderNumber;
            state.step2.sampleType.value = participant.sampleType;
            state.step2.sampleTypeNotInWork.value = participant.sampleTypeNotInWork;
            state.step2.sampleTypeOther.value = participant.sampleTypeOther;
            state.step2.sampleCollectionDate.value = participant.sampleCollectionDate === null ? "" : participant.sampleCollectionDate;
            state.step2.whoCollectedSamples.value = participant.whoCollectedSamples;
            state.step2.isExternalSampling.value = participant.isExternalSampling;
            state.step2.documentType.value = participant.documentType;
            state.step2.documentNumber.value = participant.documentNumber === null ? "" : participant.documentNumber;
            state.step2.profileResearch.value = participant.profileResearch;
        },
        makeParticipantFormValid: (state, action) => {
            state.step2.fioOrNickname.valid = true;
            state.step2.birthday.valid = true;
            state.step2.degreeOfKinship.valid = true;
            state.step2.degreeOfKinshipPreviousOrderNumber.valid = state.step2.degreeOfKinshipPreviousOrderNumber.value !== "";
            state.step2.sampleType.valid = true;
            state.step2.sampleTypeOther.valid = state.step2.sampleTypeOther.value !== "";
            state.step2.whoCollectedSamples.valid = true;
        },
        setTestParticipants: (state, action: PayloadAction<ParticipantInterface[]>) => {
            state.step2.testParticipants = action.payload.map(participant => {
                return {
                    id: `${participant.id}`,
                    parent_participant_id: participant.parent_participant_id,
                    fioOrNickname: participant.fioOrNickname,
                    gender: participant.gender_id === null ? notSelectedId : participant.gender_id,
                    birthday: participant.birthday !== null ? dayjs(participant.birthday).format("DD.MM.YYYY") : null,
                    degreeOfKinship: participant.family_link === null ? notSelectedId : participant.family_link.id,
                    degreeOfKinshipPreviousOrderNumber: participant.old_order ?? "",
                    sampleType: participant.sample === null
                        ? []
                        : participant.sample.map(unit => mapWorkingSampleFromApi(unit as ParticipantSampleApiInterface)),
                    sampleTypeNotInWork: (participant.samples_not_in_work ?? [])
                        .map(unit => mapNotInWorkSampleFromApi(unit as ParticipantSampleNotInWorkApiInterface)),
                    sampleTypeOther: participant.sampleTypeOther === null ? "" : participant.sampleTypeOther,
                    sampleCollectionDate: participant.date_sample !== null ? dayjs(participant.date_sample).format("DD.MM.YYYY") : null,
                    documentType: participant.document_type_id === null ? notSelectedId : participant.document_type_id,
                    documentNumber: participant.document_number,
                    whoCollectedSamples: participant.sampling === null ? notSelectedId : participant.sampling.id,
                    identity_document: participant.identity_document,
                    photo_documents: participant.photo_documents,
                    informed_consent_documents: participant.informed_consent_documents,
                    ready_profile_document: participant.ready_profile_document,
                    profileResearch: participant.profile_research,
                    isExternalSampling: participant.is_external_sampling,
                }
            });
        },
        cancelEditingParticipant: (state) => {
            state.step2.editingParticipantId = "";
        },
        addDocumentRow: (state, action) => {
            if (state.step3.files.length === OrderDNATestFormConfig.maxDocumentsCount) {
                return;
            }
            const doc: UploadingFileInterface = {
                id: uuidv4(),
                file: null,
                meaning: "",
            };
            state.step3.files.push(doc);
        },
        deleteDocumentRow: (state, action) => {
            const id = action.payload.id;
            state.step3.files = state.step3.files.filter(file => file.id !== id);
        },
        setFileInfo: (state, action) => {
            const id = action.payload.id;
            const {name, size} = action.payload.file;
            state.step3.files = state.step3.files.map(fileInSlice => {
                if (fileInSlice.id !== id) {
                    return fileInSlice;
                }
                fileInSlice.file = {name, size};
                return fileInSlice;
            })
        },
        setFileMeaning: (state, action) => {
            const {id, value} = action.payload;
            state.step3.files = state.step3.files.map(fileInSlice => {
                if (fileInSlice.id !== id) {
                    return fileInSlice;
                }
                fileInSlice.meaning = value;
                return fileInSlice;
            })
        },
        resetFiles: (state, action: PayloadAction<void>) => {
            const initialState = getInitialState();
            state.step3.files = initialState.step3.files;
        },
        addParticipantDocumentRow: (state) => {
            if (state.step2.participantFiles.length === OrderDNATestFormConfig.maxDocumentsCount) {
                return;
            }

            state.step2.participantFiles.push({
                id: uuidv4(),
                file: null,
                meaning: "",
            });
        },
        deleteParticipantDocumentRow: (state, action) => {
            state.step2.participantFiles = state.step2.participantFiles.filter(file => file.id !== action.payload.id);
        },
        setParticipantFileInfo: (state, action) => {
            state.step2.participantFiles = state.step2.participantFiles.map(file => {
                if (file.id !== action.payload.id) {
                    return file;
                }

                return {
                    id: file.id,
                    file: action.payload.file,
                    meaning: file.meaning,
                };
            });
        },
        setParticipantFileMeaning: (state, action) => {
            state.step2.participantFiles = state.step2.participantFiles.map(file => {
                if (file.id !== action.payload.id) {
                    return file;
                }

                return {
                    id: file.id,
                    file: file.file,
                    meaning: action.payload.value,
                };
            });
        },
        changeProductQty: (state, action: PayloadAction<DealProductInterface>) => {
            const {bitrix_id, quantity} = action.payload;
            state.step1.products.value = state.step1.products.value.map(unit => {
                if (unit.bitrix_id !== bitrix_id) {
                    return unit;
                }
                unit.quantity = quantity;
                return unit;
            });
        },
        removeProduct: (state, action: PayloadAction<DealProductInterface>) => {
            const {bitrix_id,} = action.payload;
            state.step1.products.value = state.step1.products.value.filter(unit => {
                return unit.bitrix_id !== bitrix_id;
            });
        },
        setOrderPrice: (state, action: PayloadAction<number | null>) => {
            state.orderPrice = action.payload;
        },
        setExistingProtocolGet: (state, action: PayloadAction<boolean>) => {
            state.existingProtocolGet = action.payload;
        },
        setExistingDeterminationCourt: (state, action: PayloadAction<boolean>) => {
            state.existingDeterminationCourt = action.payload;
        },
        setInitialSelections: (state, action: PayloadAction<CalculateSelectionsInterface | null>) => {
            state.initialSelections = action.payload;
        },
        setOrderFormFieldRules: (state, action: PayloadAction<OrderFormFieldRuleInterface[]>) => {
            state.orderFormFieldRules = action.payload;
        },
        setParticipantsMin: (state, action: PayloadAction<number | null>) => {
            state.participantsMin = action.payload;
        },
        setParticipantsMax: (state, action: PayloadAction<number | null>) => {
            state.participantsMax = action.payload;
        },
        setSampleMin: (state, action: PayloadAction<number | null>) => {
            state.sampleMin = action.payload;
        },
        setSampleMax: (state, action: PayloadAction<number | null>) => {
            state.sampleMax = action.payload;
        },
        setMatchingRules: (state, action: PayloadAction<AttributeRuleForNParticipantsFlatItemInterface[]>) => {
            state.step3.matchingRules = action.payload;
        },
        setMatchingRuleId: (state, action: PayloadAction<string>) => {
            state.step3.matchingRuleId = action.payload;
        },
        clearAutomaticKinshipFields: (state) => {
            state.step3.matchingRuleId = "notChosen";
            state.step3.kinshipScheme = "";
            state.step3.familyStepInDoubt = null;
            state.step3.savedKinshipCategoryRuleId = null;
            state.step1.attribute.value = "";
            state.step1.attribute.valid = false;
            state.step1.attribute.error = "";
            state.step3.describeKinshipTies.value = "";
            state.step3.describeKinshipTies.valid = false;
            state.step3.describeKinshipTies.error = "";
        },
        clearAutomaticKinshipRuleFields: (state) => {
            state.step3.matchingRuleId = "notChosen";
            state.step3.kinshipScheme = "";
            state.step3.familyStepInDoubt = null;
        },
        disableKinshipCategoryFlow: (state) => {
            state.step3.matchingRules = [];
            state.step3.matchingRuleId = "notChosen";
            state.step3.kinshipScheme = "";
            state.step3.familyStepInDoubt = null;
            state.step3.savedKinshipCategoryRuleId = null;
            state.step3.kinshipSchemaJson = null;
            state.step3.categorySelectionPath = [];
            state.step3.currentCategoryStep = 0;
            state.step3.finalSelectedRule = null;
            state.step3.describeKinshipTies.value = "";
            state.step3.describeKinshipTies.valid = false;
            state.step3.describeKinshipTies.error = "";
        },
        updateMatchingRule: (state, action: PayloadAction<updateMatchingRulePayloadAction>) => {
            state.step3.kinshipScheme = action.payload.kinshipScheme;
            state.step3.familyStepInDoubt = action.payload.familyStepInDoubt;
            state.step1.attribute.value = action.payload.attribute;
            state.step1.attribute.error = "";
            state.step1.attribute.valid = true;
        },

        // CategorySelector actions
        selectCategoryItem: (state, action: PayloadAction<{ containerId: number; itemId: number }>) => {
            const {containerId, itemId} = action.payload;
            const existingStepIndex = state.step3.categorySelectionPath.findIndex(
                step => step.containerId === containerId
            );
            if (existingStepIndex >= 0) {
                state.step3.categorySelectionPath[existingStepIndex].selectedItemId = itemId;
                state.step3.categorySelectionPath = state.step3.categorySelectionPath.slice(0, existingStepIndex + 1);
            } else {
                state.step3.categorySelectionPath.push({containerId, selectedItemId: itemId});
            }
        },
        goToNextCategoryStep: (state) => {
            state.step3.currentCategoryStep++;
        },
        goToPrevCategoryStep: (state) => {
            if (state.step3.currentCategoryStep > 0) {
                state.step3.currentCategoryStep--;
            }
            state.step3.finalSelectedRule = null;
        },
        resetCategorySelection: (state) => {
            state.step3.categorySelectionPath = [];
            state.step3.currentCategoryStep = 0;
            state.step3.finalSelectedRule = null;
            state.step3.savedKinshipCategoryRuleId = null;
        },
        resetCategorySelectionAfterRulesChange: (state) => {
            state.step3.categorySelectionPath = [];
            state.step3.currentCategoryStep = 0;
            state.step3.finalSelectedRule = null;
        },
        setFinalRule: (state, action: PayloadAction<AttributeRuleForNParticipantsFlatItemInterface>) => {
            const rule = action.payload;
            state.step3.finalSelectedRule = rule;
            state.step3.savedKinshipCategoryRuleId = rule.id;
            state.step3.kinshipScheme = rule.question?.schema ?? "";
            state.step3.familyStepInDoubt = rule.question?.family_step ?? null;
            state.step3.kinshipSchemaJson = rule.question?.pedigree_schema_json ?? null;
            state.step1.attribute.value = rule.question?.research_sign?.id
                ? String(rule.question.research_sign.id)
                : "";
            state.step1.attribute.valid = true;
            state.step1.attribute.error = "";
        },
        setSavedKinshipCategoryRuleId: (state, action: PayloadAction<number | null>) => {
            state.step3.savedKinshipCategoryRuleId = action.payload;
        },
        setKinshipSchemaJson: (state, action: PayloadAction<PedigreeSchema | null>) => {
            state.step3.kinshipSchemaJson = action.payload;
        },
        setFinalRuleFromSavedOrder: (state, action: PayloadAction<AttributeRuleForNParticipantsFlatItemInterface>) => {
            const rule = action.payload;
            state.step3.finalSelectedRule = rule;
            state.step3.savedKinshipCategoryRuleId = rule.id;
            state.step3.kinshipScheme = rule.question?.schema ?? "";
            state.step3.familyStepInDoubt = rule.question?.family_step ?? null;
            state.step1.attribute.value = rule.question?.research_sign?.id
                ? String(rule.question.research_sign.id)
                : "";
            state.step1.attribute.valid = true;
            state.step1.attribute.error = "";
        },
    }
});

export const {
    setStep,
    clearAll,
    setFormValue,
    setParticipantSampleLists,
    clearParticipantSamples,
    setAvailableSampleIds,
    setFormError,
    setFormValidation,
    showParticipantForm,
    hideParticipantForm,
    addTestParticipant,
    clearStep1,
    clearTestType,
    clearLegalStatus,
    clearAttribute,
    clearResearchPeriod,
    clearAdditionalServices,
    clearParticipantForm,
    saveEditedTestParticipant,
    setParticipantFormStatus,
    deleteTestParticipant,
    editTestParticipant,
    makeParticipantFormValid,
    setTestParticipants,
    cancelEditingParticipant,
    addDocumentRow,
    deleteDocumentRow,
    setFileInfo,
    setFileMeaning,
    resetFiles,
    addParticipantDocumentRow,
    deleteParticipantDocumentRow,
    setParticipantFileInfo,
    setParticipantFileMeaning,
    changeProductQty,
    removeProduct,
    setOrderPrice,
    setExistingProtocolGet,
    setExistingDeterminationCourt,
    setInitialSelections,
    setOrderFormFieldRules,
    setParticipantsMin,
    setParticipantsMax,
    setSampleMin,
    setSampleMax,
    setMatchingRules,
    setMatchingRuleId,
    clearAutomaticKinshipFields,
    clearAutomaticKinshipRuleFields,
    disableKinshipCategoryFlow,
    updateMatchingRule,
    setHighlightedField,
    setHighlightedParticipantId,
    // CategorySelector exports
    selectCategoryItem,
    goToNextCategoryStep,
    goToPrevCategoryStep,
    resetCategorySelection,
    resetCategorySelectionAfterRulesChange,
    setFinalRule,
    setSavedKinshipCategoryRuleId,
    setKinshipSchemaJson,
    setFinalRuleFromSavedOrder,
} = EditDNATestFormSlice.actions;

export const getSelectorForStep = (state: RootState) => state.editDNATestForm.step;

export const getSelectorForValue = <
    S extends "step1" | "step2" | "step3",
    K extends keyof EditDNATestFormState[S]
>(
    step: S,
    name: K
) => (state: RootState) => {
    const field = state.editDNATestForm[step][name] as {
        value: EditDNATestFormState[S][K] extends { value: infer V } ? V : never
    };
    return field?.value;
};

export const getSelectorForError = <
    S extends "step1" | "step2" | "step3",
    K extends keyof EditDNATestFormState[S]
>(
    step: S,
    name: K
) => (state: RootState) => {
    const field = state.editDNATestForm[step][name] as {
        error: EditDNATestFormState[S][K] extends { error: infer V } ? V : never
    };
    return field?.error;
};

export const getSelectorForValidation = <
    S extends "step1" | "step2" | "step3",
    K extends keyof EditDNATestFormState[S]
>(
    step: S,
    name: K
) => (state: RootState) => {
    const field = state.editDNATestForm[step][name] as {
        valid: EditDNATestFormState[S][K] extends { valid: infer V } ? V : never
    };
    return field?.valid;
};

export const highlightedFieldSelector = (state: RootState): string | null => state.editDNATestForm.highlightedField;

export const highlightedParticipantIdSelector = (state: RootState): string | null => state.editDNATestForm.highlightedParticipantId;

export const getSelectorForParticipantId = (state: RootState) => state.editDNATestForm.step2.participantId;


export const getSelectorForStep2FormVisibility = (state: RootState) => state.editDNATestForm.step2.isFormVisible

export const getSelectorForEditingParticipantId = (state: RootState) => state.editDNATestForm.step2.editingParticipantId

export const isFirstStepValidSelector = (state: RootState) => {
    const testTypeValid = state.editDNATestForm.step1.testType.valid;
    const testTypeOtherValid = state.editDNATestForm.step1.testTypeOther.valid;
    const orderFormFieldRules = state.editDNATestForm.orderFormFieldRules;

    const testTypeValidation = (testTypeValid && state.editDNATestForm.step1.testType.value !== "testTypeOther") || (testTypeValid && testTypeOtherValid);
    const legalStatusValid = state.editDNATestForm.step1.legalStatus.valid;
    const researchPeriodValid = state.editDNATestForm.step1.researchPeriod.valid;
    const pregnancyPeriodRequired = isFieldEnabledByRule("pregnancyPeriod", orderFormFieldRules);
    const firstTestTypeRequired = isFieldEnabledByRule("firstTestType", orderFormFieldRules);
    const pregnancyPeriodValid = !pregnancyPeriodRequired || state.editDNATestForm.step3.pregnancyPeriod.valid;
    const firstTestTypeValid = !firstTestTypeRequired || state.editDNATestForm.step3.firstTestType.valid;

    const productsValid = state.editDNATestForm.step1.products.valid;

    return testTypeValidation && legalStatusValid && researchPeriodValid && pregnancyPeriodValid && firstTestTypeValid && productsValid;
};

export const isSecondStepCompletedSelector = (state: RootState) => {
    const participants = state.editDNATestForm.step2.testParticipants;
    const mainParticipants = participants.filter(participant => !isParticipantCopy(participant));
    const orderFormFieldRules = state.editDNATestForm.orderFormFieldRules;
    const testType = state.editDNATestForm.step1.testType.value;
    const testTypeAlias = getTestTypeAlias(testType, state.editDNATestForm.initialSelections?.analyses ?? []);
    const isKinshipTestType = testTypeAlias === "rodstvo" || testTypeAlias === "kinship";
    const canShowKinshipFields = isKinshipCategoryFlowEnabled({
        isKinshipTest: isKinshipTestType,
        participantCount: mainParticipants.length,
        participantsMin: state.editDNATestForm.participantsMin,
    });

    const attributeRequired = testType !== "";
    const attributeValid = !attributeRequired || state.editDNATestForm.step1.attribute.valid;

    const describeKinshipTiesRequired = canShowKinshipFields && state.editDNATestForm.step3.matchingRules.length === 0;
    const describeKinshipTiesValid = !describeKinshipTiesRequired || state.editDNATestForm.step3.describeKinshipTies.valid;

    const kinshipSchemeFieldsValid = !canShowKinshipFields
        || state.editDNATestForm.step3.matchingRules.length === 0
        || (state.editDNATestForm.step3.kinshipScheme !== "" && state.editDNATestForm.step3.familyStepInDoubt !== null);

    const minimumCountOfParticipants = state.editDNATestForm.participantsMin;
    const participantsMinValid = minimumCountOfParticipants === null
        || minimumCountOfParticipants === infiniteCount
        || mainParticipants.length >= minimumCountOfParticipants;
    const maximumCountOfParticipants = state.editDNATestForm.participantsMax;
    const participantsMaxValid = maximumCountOfParticipants === null
        || maximumCountOfParticipants === infiniteCount
        || mainParticipants.length <= maximumCountOfParticipants;
    const minimumCountOfSamples = state.editDNATestForm.sampleMin;
    const samplesMinValid = minimumCountOfSamples === null
        || minimumCountOfSamples === infiniteCount
        || mainParticipants.every(participant => participant.sampleType.length >= minimumCountOfSamples);
    const maximumCountOfSamples = state.editDNATestForm.sampleMax;
    const samplesMaxValid = maximumCountOfSamples === null
      || maximumCountOfSamples === infiniteCount
      || mainParticipants.every(participant => participant.sampleType.length <= maximumCountOfSamples);
    const participantSamplePartsValid = participants.every(participant =>
        areParticipantSamplePartsValid(participant.sampleType)
        && areParticipantSamplePartsValid(participant.sampleTypeNotInWork, false)
    );
    const participantSamplesAreAllowed = state.editDNATestForm.availableSampleIds === null
      || participants.every(participant =>
        getInvalidParticipantSamples(
          {
            sampleType: participant.sampleType,
            sampleTypeNotInWork: participant.sampleTypeNotInWork,
          },
          state.editDNATestForm.availableSampleIds ?? [],
        ).length === 0
      );

    return mainParticipants.length > 0
        && participantsMinValid
        && participantsMaxValid
        && samplesMinValid
        && samplesMaxValid
      && participantSamplePartsValid
        && participantSamplesAreAllowed
      && attributeValid
        && describeKinshipTiesValid
        && kinshipSchemeFieldsValid;
};

export const isThirdStepValidSelector = (state: RootState) => {
    const totalSizeMb = getChosenFilesTotalSize(state) / 1000000;
    const filesTotalSizeValid = totalSizeMb <= OrderDNATestFormConfig.maxFilesSizeMb;

    const chosenFilesCount = getChosenFilesCount(state);
    const filesCountValid = chosenFilesCount <= OrderDNATestFormConfig.maxDocumentsCount;

    const filesMeaningIsValid = isMeaningOfFilesValid(state);

    const endCustomerAgreedDateOfRecordingIsValid = state.editDNATestForm.step3.endCustomerAgreedDateOfRecording.error === "";

    const legalStatusId = state.editDNATestForm.step1.legalStatus.value;
    const legalStatusStatuses = state.editDNATestForm.initialSelections?.legal_status ?? [];
    const legalStatusAlias = legalStatusStatuses.find(s => String(s.id) === String(legalStatusId))?.alias ?? "";
    const legalStatusCorrectness = isLegalStatusDocumentsValid(state, legalStatusAlias);

    return filesTotalSizeValid && filesCountValid && filesMeaningIsValid && endCustomerAgreedDateOfRecordingIsValid && legalStatusCorrectness;
}

export const participantFormStateSelector = (state: RootState) => {
    return state.editDNATestForm.step2.isUserNewOrEdit;
};

export const isParticipantFormValidSelector = (state: RootState) => {
    const fioOrNicknameValid = state.editDNATestForm.step2.fioOrNickname.valid;
    // const genderValid = state.editDNATestForm.step2.gender.value !== notSelectedId;
    const birthdayValid = state.editDNATestForm.step2.birthday.valid;

    const degreeOfKinshipValid = state.editDNATestForm.step2.degreeOfKinship.valid;
    const degreeOfKinshipPreviousOrderNumberValid = state.editDNATestForm.step2.degreeOfKinshipPreviousOrderNumber.valid;
    const degreeOfKinshipValidation = (degreeOfKinshipValid && state.editDNATestForm.step2.degreeOfKinship.value !== 39) || (degreeOfKinshipValid && degreeOfKinshipPreviousOrderNumberValid);

    const sampleTypeValid = state.editDNATestForm.step2.sampleType.valid;
    const sampleTypeOtherValid = state.editDNATestForm.step2.sampleTypeOther.valid;
    const sampleTypeValidation = (sampleTypeValid && !hasNonStandardSample(state.editDNATestForm.step2.sampleTypeNotInWork.value)) || (sampleTypeValid && sampleTypeOtherValid);
    const samplePartsValid = areParticipantSamplePartsValid([
        ...state.editDNATestForm.step2.sampleType.value,
    ]) && areParticipantSamplePartsValid(
        state.editDNATestForm.step2.sampleTypeNotInWork.value,
        false,
    );

    const sampleCollectionDateValid = state.editDNATestForm.step2.sampleCollectionDate.valid

    // const whoCollectedSamplesValid = state.editDNATestForm.step2.whoCollectedSamples.valid;

    // const documentTypeValid = state.editDNATestForm.step2.documentType.value !== notSelectedId;
    // const documentNumberValid = state.editDNATestForm.step2.documentNumber.value.trim() !== "";

    const profileResearchValid = state.editDNATestForm.step2.profileResearch.valid

    const commentForAllSamplesValid = state.editDNATestForm.step2.commentForAllSamples.valid;

    return fioOrNicknameValid &&
        // genderValid &&
        birthdayValid &&
        degreeOfKinshipValidation &&
        sampleTypeValidation &&
        samplePartsValid && profileResearchValid &&
        sampleCollectionDateValid &&
        commentForAllSamplesValid
        // whoCollectedSamplesValid &&
        // documentTypeValid &&
        // documentNumberValid
        ;
}

export const partnerFormNotEmptySelector = (state: RootState) => {

    const fioOrNickname = state.editDNATestForm.step2.fioOrNickname.value.trim();
    const gender = state.editDNATestForm.step2.gender.value;
    const birthday = state.editDNATestForm.step2.birthday.value.trim();
    const degreeOfKinship = state.editDNATestForm.step2.degreeOfKinship.value;
    const degreeOfKinshipPreviousOrderNumber = state.editDNATestForm.step2.degreeOfKinshipPreviousOrderNumber.value.trim();
    const sampleType = state.editDNATestForm.step2.sampleType.value;
    const sampleTypeOther = state.editDNATestForm.step2.sampleTypeOther.value.trim();
    const sampleCollectionDate = state.editDNATestForm.step2.sampleCollectionDate.value.trim();
    const whoCollectedSamples = state.editDNATestForm.step2.whoCollectedSamples.value;
    const documentType = state.editDNATestForm.step2.documentType.value;
    const documentNumber = state.editDNATestForm.step2.documentNumber.value.trim();

    return fioOrNickname !== "" ||
        gender !== notSelectedId ||
        birthday !== "" ||
        degreeOfKinship !== notSelectedId ||
        degreeOfKinshipPreviousOrderNumber !== "" ||
        sampleType.length > 0 ||
        sampleTypeOther !== "" ||
        sampleCollectionDate !== "" ||
        whoCollectedSamples !== notSelectedId ||
        documentType !== notSelectedId ||
        documentNumber !== ""
        ;
}

export const isParticipantFormEmptySelector = (state: RootState) => {
    const fioOrNicknameEmpty = state.editDNATestForm.step2.fioOrNickname.value.trim() === "";
    const birthdayEmpty = state.editDNATestForm.step2.birthday.value.trim() === "";
    const degreeOfKinshipNotSelected = state.editDNATestForm.step2.degreeOfKinship.value === notSelectedId;
    const degreeOfKinshipPreviousOrderNumberEmpty = state.editDNATestForm.step2.degreeOfKinshipPreviousOrderNumber.value.trim() === "";
    const sampleTypeNotSelected = state.editDNATestForm.step2.sampleType.value.length === 0;
    const sampleTypeOtherEmpty = state.editDNATestForm.step2.sampleTypeOther.value.trim() === "";
    const whoCollectedSamplesNotSelected = state.editDNATestForm.step2.whoCollectedSamples.value === notSelectedId;

    return fioOrNicknameEmpty &&
        birthdayEmpty &&
        degreeOfKinshipNotSelected &&
        degreeOfKinshipPreviousOrderNumberEmpty &&
        sampleTypeNotSelected &&
        sampleTypeOtherEmpty &&
        whoCollectedSamplesNotSelected
        ;
}

export const getSelectorForParticipants = (state: RootState) => state.editDNATestForm.step2.testParticipants;

export const getSelectorForMainParticipants = (state: RootState) =>
    state.editDNATestForm.step2.testParticipants.filter(participant => !isParticipantCopy(participant));

export const getSelectorForCopyParticipants = (state: RootState) =>
    state.editDNATestForm.step2.testParticipants.filter(isParticipantCopy);

export const countOfParticipantsSelector = (state: RootState) =>
    state.editDNATestForm.step2.testParticipants.filter(participant => !isParticipantCopy(participant)).length;

export const participantFilesSelector = (state: RootState) => state.editDNATestForm.step2.participantFiles;

export const participantDocumentsValidSelector = (state: RootState) => {
    return state.editDNATestForm.step2.participantFiles.every(file => {
        return file.file === null || file.meaning !== "";
    });
};

export const getSelectorForOrderPrice = () => (state: RootState) => {
    return state.editDNATestForm.orderPrice;
};

export const chosenFilesCountSelector = (state: RootState) => {
    return getChosenFilesCount(state);
};

export const chosenFilesSizeSelector = (state: RootState) => {
    return getChosenFilesTotalSize(state);
};

export const filesSelector = (state: RootState) => {
    return state.editDNATestForm.step3.files;
};

export const fileByIdSelector = (id: string | number) => (state: RootState) => {
    const found = state.editDNATestForm.step3.files.find((file: UploadingFileInterface) => {
        return file.id === id;
    });
    if (found === undefined) {
        throw new Error(`File with id: ${id} not found.`);
    }
    return found;
}

function isLegalStatusDocumentsValid(state: RootState, legalStatusAlias: string): boolean {
    return true;
}

function getChosenFilesTotalSize(state: RootState) {
    let size = 0;
    state.editDNATestForm.step3.files.forEach((file: UploadingFileInterface) => {
        if ("file" in file && file.file !== null) {
            size += file.file.size;
        }
    });
    return size;
}

function getChosenFilesCount(state: RootState) {
    let count = 0;
    state.editDNATestForm.step3.files.forEach((file: UploadingFileInterface) => {
        if ("file" in file && file.file !== null) {
            count++;
        }
    });
    return count;
}

function isMeaningOfFilesValid(state: RootState) {
    let valid = true;
    state.editDNATestForm.step3.files.forEach((file: UploadingFileInterface) => {
        if ("file" in file && file.file !== null && file.meaning === "") {
            valid = false;
        }
    });
    return valid;
}

export const getSelectorForInitialSelections = () => (state: RootState) =>
    state.editDNATestForm.initialSelections;

export const legalStatusAliasSelector = (state: RootState): string => {
    const legalStatusId = state.editDNATestForm.step1.legalStatus.value;
    const legalStatuses = state.editDNATestForm.initialSelections?.legal_status ?? [];

    return legalStatuses.find(status => String(status.id) === String(legalStatusId))?.alias ?? "";
};

export const participantsMinSelector = (state: RootState) => state.editDNATestForm.participantsMin;
export const participantsMaxSelector = (state: RootState) => state.editDNATestForm.participantsMax;
export const sampleMinSelector = (state: RootState) => state.editDNATestForm.sampleMin;
export const sampleMaxSelector = (state: RootState) => state.editDNATestForm.sampleMax;
export const existingProtocolGetSelector = (state: RootState) => state.editDNATestForm.existingProtocolGet;
export const existingDeterminationCourtSelector = (state: RootState) => state.editDNATestForm.existingDeterminationCourt;

export const matchingRulesSelector = (state: RootState) => state.editDNATestForm.step3.matchingRules
export const orderFormFieldRulesSelector = (state: RootState) => state.editDNATestForm.orderFormFieldRules

export const matchingRuleIdSelector = (state: RootState) => state.editDNATestForm.step3.matchingRuleId;

export const kinshipSchemeSelector = (state: RootState) => state.editDNATestForm.step3.kinshipScheme

export const familyStepInDoubtSelector = (state: RootState) => state.editDNATestForm.step3.familyStepInDoubt

const kinshipAliases = ["rodstvo", "kinship"];

export const isKinshipTestTypeSelectedSelector = (state: RootState): boolean => {
    const testTypeId = state.editDNATestForm.step1.testType.value;
    const analyses = state.editDNATestForm.initialSelections?.analyses ?? [];
    const selected = analyses.find(item => String(item.analyse.id) === String(testTypeId));
    return kinshipAliases.includes(selected?.analyse.alias ?? "");
};

// CategorySelector selectors
export const categorySelectionPathSelector = (state: RootState) =>
    state.editDNATestForm.step3.categorySelectionPath;

export const currentCategoryStepSelector = (state: RootState) =>
    state.editDNATestForm.step3.currentCategoryStep;

export const finalSelectedRuleSelector = (state: RootState) =>
    state.editDNATestForm.step3.finalSelectedRule;

export const kinshipCategoryRuleIdSelector = (state: RootState) =>
    state.editDNATestForm.step3.finalSelectedRule?.id ?? null;

export const savedKinshipCategoryRuleIdSelector = (state: RootState) =>
    state.editDNATestForm.step3.savedKinshipCategoryRuleId;

export const kinshipSchemaJsonSelector = (state: RootState) =>
    state.editDNATestForm.step3.kinshipSchemaJson;

export const isFinalStepSelector = (state: RootState) =>
    state.editDNATestForm.step3.finalSelectedRule !== null;

export const EditDNATestFormReducer = EditDNATestFormSlice.reducer;

export interface UploadingFileInterface {
    id: string,
    file: { name: string, size: number } | null,
    meaning: string,
}

export interface TestParticipantInterface {
    birthday: string | null,
    id: string,
    parent_participant_id: number | null,
    identity_document?: {
        id: number,
        name: string,
        url: string,
    }[],
    photo_documents?: {
        id: number,
        name: string,
        url: string,
    }[],
    informed_consent_documents?: {
        id: number,
        name: string,
        url: string,
    }[],
    ready_profile_document?: {
        id: number,
        name: string,
        url: string,
    }[],
    fioOrNickname: string,
    gender: number,
    degreeOfKinship: number,
    degreeOfKinshipPreviousOrderNumber: string,
    sampleType: SampleTypeInterface[],
    sampleTypeNotInWork: SampleTypeInterface[],
    sampleTypeOther: string,
    sampleCollectionDate: string | null,
    documentType: number,
    documentNumber: string | null,
    whoCollectedSamples: number,
    isExternalSampling: boolean,
    profileResearch: number,
}

export type userNewOrEdit = "new" | "edit";
